import React, { useCallback, useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { Dayjs } from 'dayjs';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer, useContainer } from 'unstated-next';

import { CLASSES_DATA_PORTAL } from '../../../../config/apiPaths';
import { getUrls } from '../../../../utils/url';
import AdminAuthContainer from '../../AdminAuthContainer';
import { filterByEndDate, filterByInstructor, filterBySearch, filterByStartDate, sortByClassName, sortByDates, sortByInstructors } from './ClassFilters/filters';
import { ClassWithInstructors, Status, Statuses, Option, InstructorWithEmail, Direction, SortBy, SortOption } from './types';

const useClasses = () => {
    const { t } = useTranslation('portal');
    const { uuid: userUuid, authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const [statuses, setStatuses] = useState<Statuses>({ fetchClasses: Status.INITIAL });

    const [classes, setClasses] = useState<ClassWithInstructors[]>([]);
    const [instructors, setInstructors] = useState<InstructorWithEmail[]>([]);

    const [selectedInstructor, setSelectedInstructor] = useState<Option | null>(null);
    const [searchValue, setSearchValue] = useState('');

    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);

    const [sortDirection, setSortDirection] = useState<Direction>(undefined);
    const [sortBy, setSortBy] = useState<SortBy>(undefined);

    const { enqueueSnackbar } = useSnackbar();

    const { api } = getUrls();

    const fetchClasses = useCallback(
        async (uuid, authModeValue, showDemoContentValue) => {
            try {
                setStatuses((previous) => ({
                    ...previous,
                    fetchClasses: Status.PENDING
                }));

                const { data } = await axios.get<ClassWithInstructors[]>(`${api}${CLASSES_DATA_PORTAL}?user_uuid=${uuid}&auth_mode=${authModeValue}&demo_content=${showDemoContentValue}`);

                const instructorMap = {};

                data.forEach((classWithInstructors) => {
                    classWithInstructors.instructors.forEach((instructor) => {
                        if (instructor.email) {
                            instructorMap[instructor.email.toLocaleLowerCase()] = instructor;
                        }
                    });
                });

                setInstructors(Object.values(instructorMap));

                setStatuses((previous) => ({
                    ...previous,
                    fetchClasses: Status.SUCCESS
                }));

                setClasses(data);
            } catch (err) {
                setStatuses((previous) => ({
                    ...previous,
                    fetchClasses: Status.ERROR
                }));

                enqueueSnackbar(t('classes-page.somethingWentWrong'), { variant: 'error' });
            }
        },
        [api, enqueueSnackbar, t]
    );

    React.useEffect(() => {
        console.log('authMode changed', authMode);
        setStatuses((previous) => ({
            ...previous,
            fetchClasses: Status.INITIAL
        }));
    }, [authMode, showDemoContent]);

    useEffect(() => {
        if (statuses.fetchClasses === Status.INITIAL && userUuid) {
            fetchClasses(userUuid, authMode, showDemoContent);
        }
    }, [fetchClasses, statuses.fetchClasses, userUuid, authMode, showDemoContent]);

    const filteredClassesByInstructor: ClassWithInstructors[] = useMemo(() => filterByInstructor({ classes, selectedInstructor }), [classes, selectedInstructor]);
    const filteredClassesBySearch: ClassWithInstructors[] = useMemo(() => filterBySearch({ classes: filteredClassesByInstructor, searchValue }), [filteredClassesByInstructor, searchValue]);
    const filteredClassesByStartDate: ClassWithInstructors[] = useMemo(() => filterByStartDate({ classes: filteredClassesBySearch, startDate }), [filteredClassesBySearch, startDate]);
    const filteredClassesByEndDate: ClassWithInstructors[] = useMemo(() => filterByEndDate({ classes: filteredClassesByStartDate, endDate }), [endDate, filteredClassesByStartDate]);

    const clonedClasses = _.cloneDeep(filteredClassesByEndDate);
    const sortedClassesByName: ClassWithInstructors[] = useMemo(() => (sortBy === SortOption.NAME ? sortByClassName(({ classes: clonedClasses, sort: sortDirection })) : clonedClasses), [clonedClasses, sortBy, sortDirection]);
    const sortedClassesByDates: ClassWithInstructors[] = useMemo(() => (sortBy === SortOption.DATES ? sortByDates(({ classes: sortedClassesByName, sort: sortDirection })) : sortedClassesByName), [sortedClassesByName, sortBy, sortDirection]);
    const filteredClasses: ClassWithInstructors[] = useMemo(() => (sortBy === SortOption.INSTRUCTOR ? sortByInstructors(({ classes: sortedClassesByDates, sort: sortDirection })) : sortedClassesByDates), [sortedClassesByDates, sortBy, sortDirection]);

    return {
        endDate,
        statuses,
        startDate,
        instructors,
        searchValue,
        setEndDate,
        setStartDate,
        setSearchValue,

        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,

        filteredClasses,
        selectedInstructor,
        setSelectedInstructor
    };
};

const ClassesContainer = createContainer(useClasses);

export default ClassesContainer;
